import React from "react";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { ChartLineDown, CurrencyEur, TreeStructure } from "@phosphor-icons/react";

const PROBLEMS = [
  {
    id: "01",
    Icon: ChartLineDown,
    title: "Decisioni senza dati chiari",
    body: "Prendi decisioni strategiche basandoti sull'intuizione perché non hai un cruscotto che ti dica dove stai vincendo e dove stai perdendo.",
  },
  {
    id: "02",
    Icon: CurrencyEur,
    title: "Costi che erodono i margini",
    body: "I costi crescono più dei ricavi. Non hai visibilità granulare su dove finiscono i soldi né su cosa è davvero produttivo.",
  },
  {
    id: "03",
    Icon: TreeStructure,
    title: "Crescita bloccata senza strategia",
    body: "L'azienda funziona ma non decolla. Servirebbe una direzione chiara, un piano scritto e priorità precise per liberare valore.",
  },
];

export default function Problema() {
  return (
    <section
      id="problema"
      data-testid="section-problema"
      className="relative py-32 md:py-40 lg:py-48 overflow-hidden"
    >
      {/* massive chapter numeral background */}
      <div
        aria-hidden
        className="absolute top-20 -left-6 md:left-10 font-display italic text-[14rem] md:text-[22rem] leading-none text-gold/[0.06] select-none pointer-events-none"
      >
        01
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <span className="w-10 h-px bg-gold" />
                <span
                  className="text-[0.7rem] uppercase text-gold font-sans-crisp font-semibold"
                  style={{ letterSpacing: "0.3em" }}
                  data-testid="problema-chapter"
                >
                  Capitolo 01 · Il Problema
                </span>
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <h2
                data-testid="problema-title"
                className="font-display font-medium tracking-tight leading-[1.05] text-4xl md:text-5xl lg:text-6xl text-ink"
              >
                Stai guidando la tua azienda{" "}
                <span className="italic-serif text-gold">senza cruscotto?</span>
              </h2>
            </Reveal>

            <Reveal delay={0.1}>
              <p className="mt-8 text-lg text-ink/70 leading-relaxed max-w-md font-sans-crisp">
                Molte PMI del Nord Est corrono senza sapere dove stanno andando.
                Riconosci uno di questi segnali?
              </p>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-7 lg:pl-8">
            <div className="grid gap-6 md:gap-8">
              {PROBLEMS.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.05 * i }}
                  className="group relative border border-gold/20 hover:border-gold/70 bg-navy-mid/30 hover:bg-navy-mid/50 backdrop-blur-sm p-8 md:p-10 transition-colors duration-700"
                  data-testid={`problema-card-${p.id}`}
                >
                  <div className="flex items-start gap-6 md:gap-8">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <span
                        className="font-display italic text-gold text-sm mb-3"
                        style={{ letterSpacing: "0.1em" }}
                      >
                        {p.id}
                      </span>
                      <div className="w-14 h-14 border border-gold/40 group-hover:border-gold flex items-center justify-center transition-colors duration-700">
                        <p.Icon size={26} weight="light" className="text-gold" />
                      </div>
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight mb-4">
                        {p.title}
                      </h3>
                      <p className="text-ink/70 leading-relaxed text-base md:text-lg font-sans-crisp">
                        {p.body}
                      </p>
                    </div>
                  </div>
                  {/* corner brackets */}
                  <span className="absolute top-3 right-3 w-3 h-px bg-gold/40 group-hover:bg-gold transition-colors" />
                  <span className="absolute top-3 right-3 h-3 w-px bg-gold/40 group-hover:bg-gold transition-colors" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
